import { Dispatch, ReactNode, SetStateAction } from 'react';

import lineTypeIcon from '@atom/components/common/svgIcons/lineTypeIcon.svg';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, ListTable, Menu } from '@atom/mui';
import { Schema } from '@atom/types/schema';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
// TODO(AM-18745): remove toggle
import { showDevelopmentFeature } from '@atom/utilities/featureToggleUtilities';
import schemaUtilities from '@atom/utilities/schemaUtilities';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

const { MenuItem } = Menu;
const {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
} = ListTable;

const tableFooterOffset = '3.3rem';

const styles = {
  tableContainer: {
    height: `calc(100% - ${tableFooterOffset})`,
  },
  link: {
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    columnGap: '0.5rem',
  },
  optionsMenuIcon: {
    marginRight: '1rem',
  },
  iconTextContainer: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '0.5rem',
  },
  emptyContent: {
    marginLeft: '0.5rem',
  },
};

interface Props {
  schemas: Partial<Schema>[];
  schemaCount: number;
  handleDuplicateSchema: (schemaId: string) => void;
  setSchemaToDelete: Dispatch<SetStateAction<Partial<Schema>>>;
  sortBy: string;
  changeSortBy: (sortTerm: string) => void;
  isSortAscending: 'asc' | 'desc';
  changeIsSortAscending: (sortDirection: 'asc' | 'desc') => void;
  page: number;
  changePage: (page: number) => void;
  limit: number;
  changeLimit: (newLimit: number) => void;
  resultsPerPageOptions: number[];
}

const SchemaPortalTable = ({
  schemas,
  schemaCount,
  handleDuplicateSchema,
  setSchemaToDelete,
  sortBy,
  changeSortBy,
  isSortAscending,
  changeIsSortAscending,
  page,
  changePage,
  limit,
  changeLimit,
  resultsPerPageOptions,
}: Props) => {
  const [, { Link }] = useNavigation();

  const locationTypeDisplay = (locationType: string): ReactNode => {
    switch (locationType) {
      case 'Point':
        return (
          <div style={styles.iconTextContainer}>
            <Icon>location_on</Icon>
            Point
          </div>
        );
      case 'LineString':
        return (
          <div style={styles.iconTextContainer}>
            <img src={lineTypeIcon} />
            Line
          </div>
        );
      default:
        return showDevelopmentFeature ? (
          <div style={styles.iconTextContainer}>
            <Icon>location_off</Icon>
            No Location
          </div>
        ) : (
          <div style={styles.emptyContent}>-</div>
        );
    }
  };

  const handleSortChange = (sortField: string, direction: 'asc' | 'desc') => {
    changeSortBy(sortField);
    changeIsSortAscending(direction);
  };

  return (
    <ListTable TableContainerProps={{ style: styles.tableContainer }}>
      <TableHead>
        <TableRow header>
          <TableCell
            variant="head"
            sortDirection={sortBy === 'name' ? isSortAscending : false}
            onSortChange={direction => handleSortChange('name', direction)}
          >
            Name
          </TableCell>
          <TableCell
            variant="head"
            // Note: backend sort field is `published`, FE property is `isPublished`
            sortDirection={sortBy === 'published' ? isSortAscending : false}
            onSortChange={direction => handleSortChange('published', direction)}
          >
            Status
          </TableCell>
          <TableCell
            variant="head"
            sortDirection={sortBy === 'locationType' ? isSortAscending : false}
            onSortChange={direction =>
              handleSortChange('locationType', direction)
            }
          >
            Location Type
          </TableCell>
          {showDevelopmentFeature && (
            <TableCell
              variant="head"
              // Note: backend sort field is `material`, FE property is `isMaterial`
              sortDirection={sortBy === 'material' ? isSortAscending : false}
              onSortChange={direction =>
                handleSortChange('material', direction)
              }
            >
              Cost Tracking
            </TableCell>
          )}
          <TableCell variant="head">Created By</TableCell>
          <TableCell
            variant="head"
            sortDirection={sortBy === 'createdDate' ? isSortAscending : false}
            onSortChange={direction =>
              handleSortChange('createdDate', direction)
            }
          >
            Created On
          </TableCell>
          <TableCell variant="head">Modified By</TableCell>
          <TableCell
            variant="head"
            sortDirection={sortBy === 'updatedDate' ? isSortAscending : false}
            onSortChange={direction =>
              handleSortChange('updatedDate', direction)
            }
          >
            Modified On
          </TableCell>
          <TableCell variant="head">
            {/* Blank for the additional options menu */}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {schemas?.map(schema => (
          <TableRow key={schema.id}>
            <TableCell>
              <Link style={styles.link} to={`/inventoryTypes/${schema.id}`}>
                {schemaUtilities.getSchemaIconFromSchemaOrAsset(schema)}
                {schema.name}
              </Link>
            </TableCell>
            <TableCell>
              <div style={styles.iconTextContainer}>
                <Icon>{schema.isPublished ? 'lock' : 'edit'}</Icon>
                {schema.isPublished ? 'Published' : 'Draft'}
              </div>
            </TableCell>
            <TableCell>{locationTypeDisplay(schema.locationType)}</TableCell>
            {showDevelopmentFeature && (
              <TableCell>{schema.isMaterial ? 'Yes' : 'No'}</TableCell>
            )}
            <TableCell>
              {schema.createdBy?.firstName} {schema.createdBy?.lastName}
            </TableCell>
            <TableCell>{setDisplayDate(schema.createdDate)}</TableCell>
            <TableCell>
              {schema.updatedBy?.firstName} {schema.updatedBy?.lastName}
            </TableCell>
            <TableCell>{setDisplayDate(schema.updatedDate)}</TableCell>
            <TableCell>
              {(schema.isPublished || hasRolePermissions(ROLE_SETS.ADMIN)) && (
                <Menu IconButtonProps={{ style: styles.optionsMenuIcon }}>
                  {schema.isPublished && (
                    <MenuItem
                      startAdornment={<Icon>content_copy</Icon>}
                      onClick={() => handleDuplicateSchema(schema.id)}
                    >
                      Duplicate
                    </MenuItem>
                  )}
                  {hasRolePermissions(ROLE_SETS.ADMIN) && (
                    <MenuItem
                      startAdornment={<Icon>delete</Icon>}
                      onClick={() => setSchemaToDelete(schema)}
                    >
                      Delete
                    </MenuItem>
                  )}
                </Menu>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TablePagination
            rowsPerPageOptions={resultsPerPageOptions}
            count={schemaCount}
            rowsPerPage={limit}
            page={page}
            onPageChange={nextPage => changePage(nextPage)}
            onRowsPerPageChange={event => {
              changeLimit(+event.target.value);
              changePage(1);
            }}
          />
        </TableRow>
      </TableFooter>
    </ListTable>
  );
};

export default SchemaPortalTable;
